// Copyright (c) Ping. All rights reserved.
//
// Pre-auth WebSocket subscriber for the device-linking flow.
//
// The new device opens this BEFORE rendering its handshake QR. After
// the existing device scans the QR and uploads a sealed LinkingTicket
// (passing the same `delivery_pubkey_hash`), the API forwards the
// sealed bytes to ws-fanout's `/v1/linking/stream` channel keyed by
// the hash, which arrive here as one Binary frame and resolve the
// promise. The flow then collapses to a single QR scan — no second
// ticket-id QR is needed.
//
// Fallback contract: if the WS errors, drops, or the timeout fires,
// the caller MUST fall back to the legacy two-step path (read the
// ticket id from the existing device, GET /v1/linking/tickets/{id}).
// That preserves behaviour for clients still on the old flow and
// for environments where ws-fanout is unreachable.

import { logger } from '../log/log';

/**
 * Sentinel value returned by `subscribeLinkingStream` when the
 * underlying WebSocket can't be constructed in this environment
 * (e.g. SSR, JSDOM without the polyfill). Caller MUST treat this
 * as "skip pre-auth path, use the legacy two-step flow".
 */
export const LINKING_STREAM_UNAVAILABLE = Symbol(
  'linking-stream/unavailable',
);

export interface LinkingStreamPayload {
  /** Sealed LinkingTicket bytes (HPKE — open via the SDK). */
  readonly sealed: Uint8Array;
  /**
   * Optional second HPKE-sealed envelope produced by the existing
   * device alongside the LinkingTicket. Carries the auth-layer
   * `user_identity_pubkey` + a BE-format `device_binding_sig` (info
   * string `ping/link-auth/v1`). Empty when the existing device is
   * on the legacy two-QR client that doesn't support link-verify.
   */
  readonly authSealed: Uint8Array;
}

export interface LinkingStreamHandle {
  /**
   * Resolves with `{sealed, authSealed}` the first time the server
   * pushes a frame. Rejects with the underlying error if the socket
   * errors before delivery, or with a `LinkingStreamTimeout` if
   * `timeoutMs` elapses with no frame.
   */
  readonly sealed: Promise<LinkingStreamPayload>;
  /**
   * Tears down the underlying WebSocket. Safe to call multiple times.
   * Resolves immediately; the WS close handshake runs in the
   * background. Call when the host unmounts the pairing screen or
   * the user manually cancels.
   */
  readonly close: () => void;
}

export class LinkingStreamTimeout extends Error {
  constructor(message = 'linking stream: no push received within timeout') {
    super(message);
    this.name = 'LinkingStreamTimeout';
  }
}

export interface LinkingStreamOptions {
  /**
   * Absolute WS URL that points at ws-fanout's
   * `/v1/linking/stream?pubkey_hash=...` route. Host derives this
   * from its own API base config — see `linkingStreamUrl` for the
   * default web shape.
   */
  readonly url: string;
  /**
   * Hard timeout in milliseconds. Defaults to 90 s — long enough
   * that an existing-device user can confirm the linking prompt on
   * a different device, short enough that the new-device user sees
   * a clear fallback prompt if the push never arrives. Matches the
   * server-side `/v1/linking/stream` idle timeout (5 min) loosely:
   * the client gives up first so the UX shows a fallback affordance
   * before the server closes.
   */
  readonly timeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 90_000;

/**
 * Opens a pre-auth WebSocket against ws-fanout's
 * `/v1/linking/stream?pubkey_hash=...` route and returns a handle
 * whose `sealed` promise resolves the first time the server pushes
 * a Binary frame. Returns `LINKING_STREAM_UNAVAILABLE` when the
 * global `WebSocket` constructor is missing.
 *
 * The caller MUST start the subscription BEFORE rendering the
 * handshake QR — otherwise the existing device's push can fan out
 * to an empty subscription set and the new device falls back to
 * the legacy path even though everything else worked.
 */
export function subscribeLinkingStream(
  opts: LinkingStreamOptions,
): LinkingStreamHandle | typeof LINKING_STREAM_UNAVAILABLE {
  if (typeof WebSocket === 'undefined') {
    return LINKING_STREAM_UNAVAILABLE;
  }
  const { url, timeoutMs = DEFAULT_TIMEOUT_MS } = opts;
  const ws = new WebSocket(url);
  ws.binaryType = 'arraybuffer';

  let settled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let resolveSealed!: (value: LinkingStreamPayload) => void;
  let rejectSealed!: (err: Error) => void;
  const sealed = new Promise<LinkingStreamPayload>((resolve, reject) => {
    resolveSealed = resolve;
    rejectSealed = reject;
  });

  const cleanup = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    try {
      ws.close();
    } catch {
      /* ignore — already closing */
    }
  };

  timer = setTimeout(() => {
    if (settled) return;
    settled = true;
    rejectSealed(new LinkingStreamTimeout());
    cleanup();
  }, timeoutMs);

  ws.addEventListener('message', (ev) => {
    if (settled) return;
    const data = ev.data as ArrayBuffer | string;
    // Parse the JSON envelope ws-fanout sends:
    //   { "sealed": "<b64url>", "auth_sealed": "<b64url>" }
    // Both halves are sent as base64url-no-pad strings inside a
    // single binary frame (utf-8 encoded JSON). `auth_sealed` is
    // empty when the existing device is on the legacy 2-QR client
    // that doesn't support link-verify.
    try {
      let text: string;
      if (typeof data === 'string') {
        text = data;
      } else {
        text = new TextDecoder().decode(new Uint8Array(data));
      }
      const parsed = JSON.parse(text) as {
        sealed?: string;
        auth_sealed?: string;
      };
      const sealedB64 = (parsed.sealed ?? '').trim();
      if (sealedB64.length === 0) {
        // Unexpected shape — keep waiting for a real frame.
        return;
      }
      const sealedBytes = base64UrlDecode(sealedB64);
      const authSealedBytes =
        parsed.auth_sealed && parsed.auth_sealed.length > 0
          ? base64UrlDecode(parsed.auth_sealed)
          : new Uint8Array(0);
      settled = true;
      resolveSealed({ sealed: sealedBytes, authSealed: authSealedBytes });
      cleanup();
    } catch (err) {
      logger.warn('linking stream frame parse failed', {
        message: (err as Error).message,
      });
    }
  });

  ws.addEventListener('error', () => {
    if (settled) return;
    logger.warn('linking stream errored');
    settled = true;
    rejectSealed(new Error('linking stream: socket error'));
    cleanup();
  });

  ws.addEventListener('close', (ev) => {
    if (settled) return;
    // Server closed before delivering — surface as a generic error
    // so the host falls back to the legacy two-step path.
    settled = true;
    rejectSealed(
      new Error(`linking stream: closed (code=${ev.code}, reason=${ev.reason})`),
    );
    cleanup();
  });

  return {
    sealed,
    close: () => {
      if (!settled) {
        settled = true;
        rejectSealed(new Error('linking stream: closed by caller'));
      }
      cleanup();
    },
  };
}

/**
 * Conventional URL derivation for callers that have an HTTP API
 * base URL (e.g. `https://api.ping.live`). Web hosts can pass
 * `window.location.origin` plus a protocol upgrade; desktop hosts
 * pass their configured API base. Either way the result targets
 * the `/v1/linking/stream` route ws-fanout reverse-proxies on the
 * same host as the API.
 */
export function linkingStreamUrl(
  apiBase: string,
  pubkeyHashB64Url: string,
): string {
  const trimmed = apiBase.replace(/\/+$/, '');
  const wsBase = trimmed.replace(/^http(s?):\/\//, (_m, s) => `ws${s}://`);
  const encoded = encodeURIComponent(pubkeyHashB64Url);
  return `${wsBase}/v1/linking/stream?pubkey_hash=${encoded}`;
}

/**
 * Computes SHA-256 of the new device's ephemeral X25519 pubkey and
 * returns the base64url-encoded (no padding) hash. Used by hosts to
 * derive both the WS subscription key and the
 * `delivery_pubkey_hash` they pass to `POST /v1/linking/tickets`.
 */
export async function pubkeyHashB64Url(ephPub: Uint8Array): Promise<string> {
  // Copy into a fresh ArrayBuffer so the runtime never sees a
  // `SharedArrayBuffer`-backed view (which the DOM digest signature
  // rejects under strict lib.dom typings).
  const buf = new ArrayBuffer(ephPub.byteLength);
  new Uint8Array(buf).set(ephPub);
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return base64UrlEncodeNoPad(new Uint8Array(digest));
}

function base64UrlDecode(s: string): Uint8Array {
  const dec = (globalThis as { atob?: (s: string) => string }).atob;
  if (typeof dec !== 'function') {
    throw new Error('base64 decoding unavailable in this environment');
  }
  const padded = s.replace(/-/g, '+').replace(/_/g, '/');
  const fill = (4 - (padded.length % 4)) % 4;
  const bin = dec(padded + '='.repeat(fill));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function base64UrlEncodeNoPad(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) {
    bin += String.fromCharCode(bytes[i]!);
  }
  // RN-macOS and modern browsers both provide `btoa`; if neither is
  // present the caller is in a stripped environment we don't
  // support for the linking flow anyway.
  const enc = (globalThis as { btoa?: (s: string) => string }).btoa;
  if (typeof enc !== 'function') {
    throw new Error('base64 encoding unavailable in this environment');
  }
  const b64 = enc(bin);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
